import { useLocation, useNavigate } from "react-router-dom";
import { 
  LayoutDashboard, 
  ShoppingCart, 
  BookOpen, 
  Wallet, 
  Menu,
  Users,
  Settings,
  Landmark,
  Shield,
  BarChart3,
  Wrench,
  ClipboardList
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useState } from "react";

const navItems = [
  { key: "dashboard", icon: LayoutDashboard, path: "/dashboard" },
  { key: "sales", icon: ShoppingCart, path: "/sales" },
  { key: "dailyBook", icon: BookOpen, path: "/daily-book" },
  { key: "expenses", icon: Wallet, path: "/expenses" },
];

const moreNavItems = [
  { key: "customers", icon: Users, path: "/customers" },
  { key: "kioskBook", icon: Landmark, path: "/kiosk-book" },
  { key: "pikVima", icon: Shield, path: "/pik-vima" },
  { key: "docSlip", icon: ClipboardList, path: "/document-slip" },
  { key: "reports", icon: BarChart3, path: "/reports" },
  { key: "services", icon: Wrench, path: "/services" },
  { key: "settings", icon: Settings, path: "/settings" },
];

export function MobileBottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [sheetOpen, setSheetOpen] = useState(false);

  const isActive = (path: string) => location.pathname === path;
  const isMoreActive = moreNavItems.some(item => isActive(item.path));

  const handleNavClick = (path: string) => {
    navigate(path);
  };

  const handleMoreItemClick = (path: string) => {
    setSheetOpen(false);
    setTimeout(() => navigate(path), 150);
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 md:hidden">
      {/* Glass effect background with safe area padding */}
      <div className="absolute inset-0 bg-background/95 backdrop-blur-xl border-t border-border shadow-lg" />
      
      <div className="relative flex items-center justify-around h-16 px-1 pb-safe">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          
          return (
            <button
              key={item.key}
              onClick={() => handleNavClick(item.path)}
              className={cn(
                "flex flex-col items-center justify-center flex-1 h-full gap-0.5 transition-all duration-200 touch-manipulation active:scale-95",
                active 
                  ? "text-primary" 
                  : "text-muted-foreground active:text-foreground"
              )}
            >
              <div className={cn(
                "flex items-center justify-center w-12 h-8 rounded-2xl transition-all duration-300",
                active && "bg-primary/15 scale-105"
              )}>
                <Icon className={cn(
                  "h-5 w-5 transition-transform duration-200",
                  active && "scale-110"
                )} strokeWidth={active ? 2.5 : 2} />
              </div>
              <span className={cn(
                "text-[10px] font-medium truncate max-w-[60px] transition-all duration-200",
                active && "font-semibold"
              )}>
                {t(item.key as any)}
              </span>
            </button>
          );
        })}
        
        {/* More button with Sheet */}
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetTrigger asChild>
            <button className={cn(
              "flex flex-col items-center justify-center flex-1 h-full gap-0.5 transition-all duration-200 touch-manipulation active:scale-95",
              isMoreActive 
                ? "text-primary" 
                : "text-muted-foreground active:text-foreground"
            )}>
              <div className={cn(
                "flex items-center justify-center w-12 h-8 rounded-2xl transition-all duration-300",
                isMoreActive && "bg-primary/15 scale-105"
              )}>
                <Menu className={cn(
                  "h-5 w-5 transition-transform duration-200",
                  sheetOpen && "rotate-90"
                )} strokeWidth={isMoreActive ? 2.5 : 2} />
              </div>
              <span className={cn(
                "text-[10px] font-medium",
                isMoreActive && "font-semibold"
              )}>
                {t("more" as any) || "More"}
              </span>
            </button>
          </SheetTrigger>
          <SheetContent side="bottom" className="rounded-t-3xl px-4 pb-8">
            {/* Drag indicator */}
            <div className="flex justify-center pt-2 pb-4">
              <div className="w-10 h-1 rounded-full bg-muted-foreground/30" />
            </div>
            <SheetHeader className="pb-4">
              <SheetTitle className="text-lg font-bold">{t("menu" as any) || "Menu"}</SheetTitle>
            </SheetHeader>
            <div className="grid grid-cols-3 gap-3">
              {moreNavItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                
                return (
                  <button
                    key={item.key}
                    onClick={() => handleMoreItemClick(item.path)}
                    className={cn(
                      "flex flex-col items-center justify-center gap-2 p-4 rounded-2xl transition-all duration-200 touch-manipulation active:scale-95",
                      active 
                        ? "bg-primary/15 text-primary" 
                        : "bg-muted/50 hover:bg-muted text-muted-foreground hover:text-foreground"
                    )}
                  >
                    <div className={cn(
                      "flex items-center justify-center w-10 h-10 rounded-xl transition-colors",
                      active ? "bg-primary/20" : "bg-background"
                    )}>
                      <Icon className="h-5 w-5" strokeWidth={active ? 2.5 : 2} />
                    </div>
                    <span className={cn(
                      "text-xs font-medium text-center leading-tight",
                      active && "font-semibold"
                    )}>
                      {t(item.key as any)}
                    </span>
                  </button>
                );
              })}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </nav>
  );
}
